/**
 * Venus Liquidity Hub — the read surface the `venus-hub` reader calls.
 *
 * The Hub is a per-asset ERC-4626 allocator: one share token per underlying,
 * cascading deposits across YieldGroups (Venus Core vTokens, Fluid fTokens,
 * Venus Fixed-Rate Vaults). Everything below is `view`; the reader issues all
 * of it in a single multicall round, which is why the group and market
 * addresses are pinned on the registry entry rather than discovered — a
 * discovered address cannot be a call target in the same round.
 *
 * Source (Sourcify `exact_match` on chain 56): Hub `0x44BCabAF2e…`,
 * YieldGroup `0x3BccED778C…`, AdapterCoreV1 `0x4E514a0C7a…`.
 */

/**
 * Hub-level views beyond the plain ERC-4626 ones.
 *
 *  - `maxDeposit(address)` ignores its argument and returns the aggregate
 *    spare capacity across unpaused YieldGroups — a real, finite cap on every
 *    live Hub, so it feeds `depositCapacity` rather than being dropped.
 *  - `hubPaused()` zeroes both `maxDeposit` and `maxWithdraw` on the contract;
 *    the reader has to apply it by hand because it computes liquidity from the
 *    groups instead of from `maxWithdraw` (which is owner-clamped and so reads
 *    0 for any address that holds no shares — including a probe).
 *  - `maxWithdrawalSize()` is a per-TRANSACTION asset cap, deliberately not a
 *    property of the vault's liquidity. It binds `liquidity` because that
 *    field is "what a holder can pull out this block", and a single
 *    transaction is what a holder gets.
 *  - `redeemFeeBps()` is retained IN the vault (it lifts the remaining
 *    holders' share price), not paid to the protocol — 0 on all three Hubs
 *    today, and read live because `setRedeemFeeBps` is an ACM-gated dial.
 */
export const VenusHubReadAbi = [
  {
    name: 'maxDeposit',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'hubPaused',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'bool' }],
  },
  {
    name: 'maxWithdrawalSize',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'redeemFeeBps',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint16' }],
  },
  {
    name: 'registeredYieldGroups',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address[]' }],
  },
] as const

/**
 * YieldGroup — one per yield family per Hub.
 *
 *  - `maxWithdraw()` (NO argument — distinct from the Hub's owner-clamped
 *    `maxWithdraw(address)`) is the family's liquid funds. It is the whole
 *    reason this reader exists: `AdapterFRV.maxWithdraw` returns **0 outside
 *    terminal states**, so a Fixed-Rate Vault's assets sit in `totalAssets()`
 *    and are not withdrawable until it matures. Reporting `totalAssets` as
 *    liquidity would advertise an exit that reverts for ~20 % of vhUSDT.
 *  - `spotAPYBps()` is the group's TVL-weighted supply rate. Despite the name
 *    it is a nominal **APR**: `AdapterCoreV1` computes
 *    `supplyRatePerBlock × blocksPerYear` and `AdapterFlux` sums Fluid's
 *    pre-annualised `supplyRate + rewardsRate/1e10`. That matches this
 *    package's convention, so it needs no APY→APR conversion.
 *  - `blocksPerYear()` is the annualiser the Core adapter uses. Read rather
 *    than pinned: BNB Chain answers **70_080_000** (0.45 s blocks) against the
 *    ~10_512_000 the adapter's own natspec still cites, and it is a settable
 *    dial.
 */
export const VenusYieldGroupReadAbi = [
  {
    name: 'maxWithdraw',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'totalAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'spotAPYBps',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint64' }],
  },
  {
    name: 'blocksPerYear',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'resources',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address[]' }],
  },
] as const

/**
 * The Venus Core Comptroller's XVS emission dial, supply side.
 *
 * XVS per BLOCK distributed to suppliers of `vToken`. Non-zero here does NOT
 * mean Hub depositors earn it — see `strandedRewardsRate` on
 * [`SavingsVault`](../types.ts) for why this is read at all.
 *
 * The BNB Core pool is a Diamond and exposes no `getRewardDistributors()`, so
 * `venusSupplySpeeds` is the complete supply-side emission surface for it.
 */
export const VenusComptrollerRewardsAbi = [
  {
    name: 'venusSupplySpeeds',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'oracle',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }],
  },
] as const

/**
 * Venus `ResilientOracle`.
 *
 * Both getters return USD scaled by `1e(36 − decimals(asset))`, the Compound
 * convention — so the USD value of a raw amount is
 * `rawAmount × price / 1e36` regardless of the asset's decimals, and no
 * per-asset scaling table is needed.
 *
 * `getPrice` takes a plain asset (XVS); `getUnderlyingPrice` takes a vToken
 * and prices its underlying.
 */
export const VenusResilientOracleAbi = [
  {
    name: 'getPrice',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'getUnderlyingPrice',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

/**
 * vToken size reads, for the emission-APR denominator.
 *
 * `totalSupply() × exchangeRateStored() / 1e18` is the market's supplied
 * underlying in raw units. `exchangeRateStored` is pre-accrual, which understates
 * the denominator by a block's interest — immaterial against an emission rate,
 * and the pre-accrual read is what keeps this a pure `view`.
 */
export const VTokenSizeReadAbi = [
  {
    name: 'totalSupply',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'exchangeRateStored',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'comptroller',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }],
  },
] as const

/**
 * `HubRegistry` — the chain-level singleton that enumerates every Hub.
 *
 * Not on the reader's hot path (the registry rows are curated, as everywhere
 * else in `SAVINGS_REGISTRY`), but it is what the live test asserts our
 * pinned roster against, so a fourth Hub cannot ship unnoticed.
 */
export const VenusHubRegistryAbi = [
  {
    name: 'getHubs',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address[]' }],
  },
  {
    name: 'hubForAsset',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address' }],
    outputs: [{ type: 'address' }],
  },
] as const

/**
 * Venus **Fixed-Rate Vault** (`InstitutionalLoanVault`) — the collateral view.
 *
 * Read only by the live test that guards the rows' `overcollateralized` label.
 * The FRV is where that label is least obvious: its documentation reads like
 * unsecured credit ("principal and target interest are not guaranteed"), while
 * the contract carries a `RiskConfig` with a liquidation threshold and an
 * HF-based liquidation path. The struct getters are what settle it.
 *
 * `institutionalConfig()` → `(collateralAsset, idealCollateralAmount,
 * marginRate, institutionOperator, positionTokenId)`;
 * `institutionalRuntime()` → `(totalCollateralDeposited,
 * minimumCollateralRequired, confiscatedMarginRemaining,
 * institutionDefaulted)`. Field order mirrors
 * `VenusProtocol/fixed-rate-vaults` `IInstitutionalVaultTypes.sol` and, like
 * the Hub's own adapter interface, is load-bearing.
 */
export const VenusFrvCollateralAbi = [
  {
    name: 'institutionalConfig',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      {
        type: 'tuple',
        components: [
          { name: 'collateralAsset', type: 'address' },
          { name: 'idealCollateralAmount', type: 'uint256' },
          { name: 'marginRate', type: 'uint256' },
          { name: 'institutionOperator', type: 'address' },
          { name: 'positionTokenId', type: 'uint256' },
        ],
      },
    ],
  },
  {
    name: 'institutionalRuntime',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      {
        type: 'tuple',
        components: [
          { name: 'totalCollateralDeposited', type: 'uint256' },
          { name: 'minimumCollateralRequired', type: 'uint256' },
          { name: 'confiscatedMarginRemaining', type: 'uint256' },
          { name: 'institutionDefaulted', type: 'bool' },
        ],
      },
    ],
  },
  {
    name: 'riskConfig',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      {
        type: 'tuple',
        components: [
          { name: 'liquidationThreshold', type: 'uint256' },
          { name: 'liquidationIncentive', type: 'uint256' },
          { name: 'latePenaltyRate', type: 'uint256' },
        ],
      },
    ],
  },
  {
    name: 'outstandingDebt',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const
